// src/pages/Privacy.jsx — PhsarGame Privacy Policy
import { Link } from "react-router-dom";
import Navbar from "../components/common/Navbar";
import { useLang } from "../context/LanguagesContext";

const S = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
*, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
.legal-pg { background:#121212; color:#fff; font-family:'Inter',sans-serif; min-height:100vh; overflow-x:hidden; }

/* ── HERO BANNER ── */
.legal-hero {
  background:#0d0d0d; border-bottom:1px solid rgba(255,255,255,0.06);
  padding:120px 80px 56px; position:relative; overflow:hidden;
}
.legal-hero-grid {
  position:absolute; inset:0; z-index:0;
  background-image:
    linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px);
  background-size:48px 48px;
}
.legal-hero-glow {
  position:absolute; z-index:1; top:-30%; right:10%;
  width:600px; height:400px;
  background:radial-gradient(ellipse, rgba(0,120,242,0.08) 0%, transparent 70%);
  pointer-events:none;
}
.legal-hero-inner { max-width:900px; margin:0 auto; position:relative; z-index:2; }
.legal-tag {
  display:inline-flex; align-items:center; gap:8px;
  background:rgba(0,120,242,0.1); border:1px solid rgba(0,120,242,0.25);
  border-radius:4px; padding:5px 12px; margin-bottom:20px;
  font-size:11px; font-weight:700; color:#0078F2; letter-spacing:1.2px; text-transform:uppercase;
}
.legal-hero h1 {
  font-size:clamp(32px,4.5vw,56px); font-weight:900; letter-spacing:-1.5px;
  color:#fff; margin-bottom:16px; line-height:1.08;
}
.legal-hero h1 span { color:#0078F2; }
.legal-hero-sub { font-size:15px; color:#6b6b6b; line-height:1.65; max-width:560px; }
.legal-hero-meta {
  display:flex; align-items:center; gap:20px; margin-top:28px; flex-wrap:wrap;
}
.legal-meta-item { font-size:12px; color:#4a4a4a; display:flex; align-items:center; gap:6px; }
.legal-meta-dot { width:4px; height:4px; background:#0078F2; border-radius:50%; }

/* ── LAYOUT ── */
.legal-layout {
  max-width:1100px; margin:0 auto; padding:60px 80px 100px;
  display:grid; grid-template-columns:220px 1fr; gap:56px; align-items:start;
}

/* Sticky TOC */
.legal-toc { position:sticky; top:84px; }
.legal-toc-title { font-size:11px; font-weight:700; color:#3a3a3a; text-transform:uppercase; letter-spacing:1.2px; margin-bottom:16px; }
.legal-toc-list { list-style:none; display:flex; flex-direction:column; gap:2px; }
.legal-toc-item a {
  display:block; font-size:13px; font-weight:500; color:#6b6b6b;
  text-decoration:none; padding:7px 12px; border-radius:6px;
  border-left:2px solid transparent; transition:all 0.15s;
}
.legal-toc-item a:hover { color:#fff; background:rgba(255,255,255,0.04); border-left-color:#0078F2; }

/* ── CONTENT ── */
.legal-content { min-width:0; }
.legal-section { margin-bottom:52px; scroll-margin-top:90px; }
.legal-section-num {
  font-size:11px; font-weight:700; color:#0078F2; text-transform:uppercase;
  letter-spacing:1.2px; margin-bottom:10px;
}
.legal-section h2 {
  font-size:22px; font-weight:800; color:#fff; letter-spacing:-0.4px;
  margin-bottom:18px; line-height:1.2;
  padding-bottom:14px; border-bottom:1px solid rgba(255,255,255,0.06);
}
.legal-section p {
  font-size:14px; color:#8a8a8a; line-height:1.8; margin-bottom:14px;
}
.legal-section p:last-child { margin-bottom:0; }
.legal-section ul {
  list-style:none; display:flex; flex-direction:column; gap:10px; margin-bottom:14px;
}
.legal-section ul li {
  font-size:14px; color:#8a8a8a; line-height:1.75;
  padding-left:20px; position:relative;
}
.legal-section ul li::before {
  content:''; position:absolute; left:0; top:9px;
  width:5px; height:5px; background:#22c55e; border-radius:50%;
}
.legal-highlight {
  background:rgba(34,197,94,0.06); border:1px solid rgba(34,197,94,0.15);
  border-left:3px solid #22c55e; border-radius:0 6px 6px 0;
  padding:14px 18px; margin:16px 0;
  font-size:13px; color:#86efac; line-height:1.7;
}

/* Data table */
.data-table { width:100%; border-collapse:collapse; margin:16px 0; }
.data-table th {
  font-size:11px; font-weight:700; color:#6b6b6b; text-transform:uppercase;
  letter-spacing:0.8px; text-align:left; padding:10px 14px;
  background:#1a1a1a; border-bottom:1px solid rgba(255,255,255,0.06);
}
.data-table td {
  font-size:13px; color:#8a8a8a; padding:12px 14px;
  border-bottom:1px solid rgba(255,255,255,0.04); vertical-align:top;
}
.data-table td:first-child { color:#e0e0e0; font-weight:500; }
.data-table tr:last-child td { border-bottom:none; }

/* ── CONTACT CARD ── */
.legal-contact {
  background:#1a1a1a; border:1px solid rgba(255,255,255,0.08); border-radius:10px;
  padding:28px; margin-top:56px; display:flex; align-items:center;
  justify-content:space-between; gap:24px; flex-wrap:wrap;
}
.legal-contact-left h3 { font-size:16px; font-weight:700; color:#fff; margin-bottom:6px; }
.legal-contact-left p { font-size:13px; color:#6b6b6b; }
.legal-contact-btn {
  font-family:'Inter',sans-serif; font-size:13px; font-weight:700;
  color:#fff; background:#0078F2; border:none; border-radius:6px;
  padding:10px 22px; cursor:pointer; text-decoration:none; transition:all 0.15s; white-space:nowrap;
}
.legal-contact-btn:hover { background:#0a84ff; }

@media(max-width:900px) {
  .legal-hero { padding:100px 40px 44px; }
  .legal-layout { grid-template-columns:1fr; padding:40px 40px 80px; gap:32px; }
  .legal-toc { display:none; }
  .data-table { font-size:12px; }
}
@media(max-width:580px) {
  .legal-hero { padding:90px 20px 36px; }
  .legal-layout { padding:32px 20px 64px; }
}
`;

const CONTENT = {
  en: {
    tag: "Legal Document",
    title: "Privacy",
    accent: "Policy",
    sub: "Your privacy is important to us. This policy explains what data we collect, how we use it, and your rights regarding your personal information when using PhsarGame.",
    updated: "Last updated: January 2025",
    effective: "Effective immediately",
    toc: [
      { id:"overview",    label:"Overview" },
      { id:"collection",  label:"Data We Collect" },
      { id:"usage",       label:"How We Use Your Data" },
      { id:"sharing",     label:"Data Sharing" },
      { id:"storage",     label:"Data Storage & Security" },
      { id:"cookies",     label:"Cookies" },
      { id:"rights",      label:"Your Rights" },
      { id:"children",    label:"Children's Privacy" },
      { id:"third-party", label:"Third-Party Services" },
      { id:"contact",     label:"Contact Us" },
    ],
    sections: [
      {
        id:"overview", num:"01", title:"Overview",
        body: [
          { type:"p", text:"PhsarGame ('we', 'us', or 'our') is committed to protecting your personal information. This Privacy Policy describes how we collect, use, disclose, and safeguard your information when you use our digital game store." },
          { type:"p", text:"By using PhsarGame, you consent to the data practices described in this policy. If you do not agree with the terms of this policy, please do not use our services." },
          { type:"highlight", text:"🔒 We do not sell your personal data to third parties. Your information is used solely to operate and improve our services." },
        ]
      },
      {
        id:"collection", num:"02", title:"Data We Collect",
        body: [
          { type:"p", text:"We collect the following types of information:" },
          { type:"table", rows:[
            ["Account Information", "Email address, display name, password (hashed)"],
            ["Purchase Data", "Order history, payment timestamps, product IDs"],
            ["Payment Data", "ABA Pay transaction reference numbers only — we never store card numbers or full banking details"],
            ["Device & Usage", "Browser type, IP address, pages visited, session duration"],
            ["Chat Messages", "Support conversations via our in-app chat"],
            ["Cookies", "Session tokens and language preferences"],
          ]},
          { type:"p", text:"We do not collect sensitive personal data such as national ID numbers, biometric data, or precise location data." },
        ]
      },
      {
        id:"usage", num:"03", title:"How We Use Your Data",
        body: [
          { type:"p", text:"We use the information we collect to:" },
          { type:"ul", items:[
            "Create and manage your account",
            "Process and fulfill your orders",
            "Send order confirmations and delivery details via email or Telegram",
            "Respond to customer support inquiries",
            "Detect and prevent fraudulent transactions",
            "Improve our website and service quality",
            "Comply with legal obligations",
          ]},
          { type:"p", text:"We will only use your data for the purposes listed above. We will not use your personal information for automated decision-making or profiling that significantly affects you." },
        ]
      },
      {
        id:"sharing", num:"04", title:"Data Sharing",
        body: [
          { type:"p", text:"We do not sell, trade, or rent your personal data. We may share limited information with:" },
          { type:"ul", items:[
            "Firebase (Google) — for authentication and database hosting",
            "ABA Bank — for payment processing (transaction reference only)",
            "Law enforcement — if required by Cambodian law or court order",
          ]},
          { type:"highlight", text:"✅ We never share your email, order history, or chat messages with advertisers, marketing companies, or any other commercial third party." },
        ]
      },
      {
        id:"storage", num:"05", title:"Data Storage & Security",
        body: [
          { type:"p", text:"Your data is stored securely on Google Firebase servers, which are hosted in data centers with enterprise-grade security measures including encryption at rest and in transit." },
          { type:"ul", items:[
            "Passwords are hashed using Firebase Authentication — we never see your plain-text password",
            "All data transmission uses HTTPS/TLS encryption",
            "Access to your data is restricted to authorized PhsarGame staff only",
            "We retain your data for as long as your account is active or as needed to comply with legal obligations",
          ]},
          { type:"p", text:"Despite our best efforts, no security system is impenetrable. In the event of a data breach, we will notify affected users promptly." },
        ]
      },
      {
        id:"cookies", num:"06", title:"Cookies",
        body: [
          { type:"p", text:"We use minimal cookies and browser storage to operate our service:" },
          { type:"ul", items:[
            "Session cookies to keep you logged in",
            "Language preference stored in localStorage (pg_lang)",
            "Firebase authentication tokens",
          ]},
          { type:"p", text:"We do not use advertising cookies, tracking pixels, or third-party analytics cookies. You can clear cookies through your browser settings at any time, but this may log you out of your account." },
        ]
      },
      {
        id:"rights", num:"07", title:"Your Rights",
        body: [
          { type:"p", text:"You have the following rights regarding your personal data:" },
          { type:"ul", items:[
            "Access — request a copy of the personal data we hold about you",
            "Correction — request correction of inaccurate or incomplete data",
            "Deletion — request deletion of your account and associated data",
            "Portability — request your data in a portable format",
            "Objection — object to how we process your data",
          ]},
          { type:"p", text:"To exercise any of these rights, contact us via Telegram @phsargame or our Facebook page. We will respond within 30 days." },
        ]
      },
      {
        id:"children", num:"08", title:"Children's Privacy",
        body: [
          { type:"p", text:"PhsarGame is not directed to children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us and we will delete it promptly." },
          { type:"p", text:"Users between the ages of 13 and 18 must have parental consent to use our services and make purchases." },
        ]
      },
      {
        id:"third-party", num:"09", title:"Third-Party Services",
        body: [
          { type:"p", text:"Our service integrates with the following third-party platforms:" },
          { type:"ul", items:[
            "Google Firebase — authentication and data storage (Google Privacy Policy applies)",
            "ABA Bank — payment processing (ABA Privacy Policy applies)",
            "Telegram — optional order delivery and support channel",
            "YouTube — embedded tutorial videos on the How to Use page",
          ]},
          { type:"p", text:"We are not responsible for the privacy practices of these third-party services. We encourage you to review their privacy policies." },
        ]
      },
      {
        id:"contact", num:"10", title:"Contact Us",
        body: [
          { type:"p", text:"If you have questions or concerns about this Privacy Policy or our data practices, you can reach us at:" },
          { type:"ul", items:[
            "Telegram: @phsargame",
            "Facebook: facebook.com/phsargame",
            "In-app chat: available on our website",
          ]},
          { type:"p", text:"We are committed to resolving any privacy concerns promptly and transparently. This policy was last reviewed in January 2025." },
        ]
      },
    ],
    contactTitle: "Questions about your privacy?",
    contactSub: "We take privacy seriously. Contact us and we'll respond within 24 hours.",
    contactBtn: "Contact Us",
  },

  km: {
    tag: "ឯកសារច្បាប់",
    title: "គោលការណ៍",
    accent: "ភាពឯកជន",
    sub: "ភាពឯកជនរបស់អ្នកមានសារៈសំខាន់ចំពោះយើង។ គោលការណ៍នេះពន្យល់ពីទិន្នន័យដែលយើងប្រមូល របៀបដែលយើងប្រើប្រាស់វា និងសិទ្ធិរបស់អ្នកទាក់ទងនឹងព័ត៌មានផ្ទាល់ខ្លួនរបស់អ្នក។",
    updated: "ធ្វើបច្ចុប្បន្នភាពចុងក្រោយ៖ មករា ២០២៥",
    effective: "មានប្រសិទ្ធភាពភ្លាមៗ",
    toc: [
      { id:"overview",    label:"ទិដ្ឋភាពទូទៅ" },
      { id:"collection",  label:"ទិន្នន័យដែលយើងប្រមូល" },
      { id:"usage",       label:"របៀបប្រើប្រាស់ទិន្នន័យ" },
      { id:"sharing",     label:"ការចែករំលែកទិន្នន័យ" },
      { id:"storage",     label:"ការរក្សាទុក និងសុវត្ថិភាព" },
      { id:"cookies",     label:"Cookies" },
      { id:"rights",      label:"សិទ្ធិរបស់អ្នក" },
      { id:"children",    label:"ភាពឯកជនរបស់កុមារ" },
      { id:"third-party", label:"សេវាភាគីទីបី" },
      { id:"contact",     label:"ទូទាក់យើង" },
    ],
    sections: [
      {
        id:"overview", num:"០១", title:"ទិដ្ឋភាពទូទៅ",
        body: [
          { type:"p", text:"PhsarGame ('យើង', 'ពួកយើង') ប្តេជ្ញាថានឹងការពារព័ត៌មានផ្ទាល់ខ្លួនរបស់អ្នក។ គោលការណ៍ភាពឯកជននេះពណ៌នាពីរបៀបដែលយើងប្រមូល ប្រើ បង្ហាញ និងការពារព័ត៌មានរបស់អ្នកនៅពេលអ្នកប្រើហាងហ្គេមឌីជីថលរបស់យើង។" },
          { type:"p", text:"ដោយការប្រើ PhsarGame អ្នកយល់ព្រមចំពោះការអនុវត្តទិន្នន័យដែលបានពណ៌នានៅក្នុងគោលការណ៍នេះ។ ប្រសិនបើអ្នកមិនយល់ព្រមនឹងលក្ខខណ្ឌនៃគោលការណ៍នេះ សូមមកកុំប្រើសេវាកម្មរបស់យើង។" },
          { type:"highlight", text:"🔒 យើងមិនលក់ទិន្នន័យផ្ទាល់ខ្លួនរបស់អ្នកទៅភាគីទីបីទេ។ ព័ត៌មានរបស់អ្នកត្រូវបានប្រើតែសម្រាប់ដំណើរការ និងកែលម្អសេវាកម្មរបស់យើងប៉ុណ្ណោះ។" },
        ]
      },
      {
        id:"collection", num:"០២", title:"ទិន្នន័យដែលយើងប្រមូល",
        body: [
          { type:"p", text:"យើងប្រមូលប្រភេទព័ត៌មានខាងក្រោម:" },
          { type:"table", rows:[
            ["ព័ត៌មានគណនី", "អ៊ីមែល ឈ្មោះបង្ហាញ ពាក្យសម្ងាត់ (ត្រូវបាន hash)"],
            ["ទិន្នន័យការទិញ", "ប្រវត្តិបញ្ជាទិញ ពេលវេលាបង់ប្រាក់ ID ផលិតផល"],
            ["ទិន្នន័យការទូទាត់", "លេខយោងប្រតិបត្តិការ ABA Pay តែប៉ុណ្ណោះ — យើងមិនដែលរក្សាទុកលេខកាត ឬព័ត៌មានធនាគារពេញទេ"],
            ["ឧបករណ៍ និងការប្រើ", "ប្រភេទ browser អាសយដ្ឋាន IP ទំព័រដែលបានចូលមើល រយៈពេលវគ្គ"],
            ["សារជជែក", "ការសន្ទនាជំនួយតាម chat ក្នុងកម្មវិធី"],
            ["Cookies", "token វគ្គ និងចំណូលចិត្តភាសា"],
          ]},
          { type:"p", text:"យើងមិនប្រមូលទិន្នន័យផ្ទាល់ខ្លួនដែលរសើបដូចជាលេខអត្តសញ្ញាណ ទិន្នន័យជីវមាត្រ ឬទិន្នន័យទីតាំងត្រឹមត្រូវទេ។" },
        ]
      },
      {
        id:"usage", num:"០៣", title:"របៀបដែលយើងប្រើប្រាស់ទិន្នន័យរបស់អ្នក",
        body: [
          { type:"p", text:"យើងប្រើប្រាស់ព័ត៌មានដែលយើងប្រមូលដើម្បី:" },
          { type:"ul", items:[
            "បង្កើត និងគ្រប់គ្រងគណនីរបស់អ្នក",
            "ដំណើរការ និងបំពេញការបញ្ជាទិញរបស់អ្នក",
            "ផ្ញើការបញ្ជាក់ការបញ្ជាទិញ និងព័ត៌មានពីការដឹកជញ្ជូនតាមអ៊ីមែល ឬ Telegram",
            "ឆ្លើយតបទៅនឹងការស្នើសុំជំនួយអតិថិជន",
            "រកឃើញ និងទប់ស្កាត់ប្រតិបត្តិការបោកប្រាស់",
            "កែលម្អគេហទំព័រ និងគុណភាពសេវាកម្មរបស់យើង",
            "អនុលោមតាមកាតព្វកិច្ចផ្លូវច្បាប់",
          ]},
          { type:"p", text:"យើងនឹងប្រើទិន្នន័យរបស់អ្នកតែសម្រាប់គោលបំណងដែលបានរាយបញ្ជីខាងលើ។ យើងនឹងមិនប្រើព័ត៌មានផ្ទាល់ខ្លួនរបស់អ្នកសម្រាប់ការសម្រេចចិត្តដោយស្វ័យប្រវត្តិ ឬការបង្ករូបដែលប៉ះពាល់ដល់អ្នកយ៉ាងខ្លាំងទេ។" },
        ]
      },
      {
        id:"sharing", num:"០៤", title:"ការចែករំលែកទិន្នន័យ",
        body: [
          { type:"p", text:"យើងមិនលក់ ជួញដូរ ឬជួលទិន្នន័យផ្ទាល់ខ្លួនរបស់អ្នកទេ។ យើងអាចចែករំលែកព័ត៌មានដែលមានកំណត់ជាមួយ:" },
          { type:"ul", items:[
            "Firebase (Google) — សម្រាប់ការផ្ទៀងផ្ទាត់ និងការបង្ហោះមូលដ្ឋានទិន្នន័យ",
            "ធនាគារ ABA — សម្រាប់ដំណើរការការទូទាត់ (លេខយោងប្រតិបត្តិការតែប៉ុណ្ណោះ)",
            "ស្ថាប័នអនុវត្តច្បាប់ — ប្រសិនបើតម្រូវដោយច្បាប់កម្ពុជា ឬដីកា",
          ]},
          { type:"highlight", text:"✅ យើងមិនដែលចែករំលែកអ៊ីមែល ប្រវត្តិបញ្ជាទិញ ឬសារជជែករបស់អ្នកជាមួយអ្នកផ្សាយពាណិជ្ជកម្ម ក្រុមហ៊ុនទីផ្សារ ឬភាគីពាណិជ្ជកម្មទីបីផ្សេងៗទេ។" },
        ]
      },
      {
        id:"storage", num:"០៥", title:"ការរក្សាទុក និងសុវត្ថិភាពទិន្នន័យ",
        body: [
          { type:"p", text:"ទិន្នន័យរបស់អ្នកត្រូវបានរក្សាទុកដោយសុវត្ថិភាពនៅលើម៉ាស៊ីនមេ Google Firebase ដែលត្រូវបានបង្ហោះនៅក្នុងមជ្ឈមណ្ឌលទិន្នន័យជាមួយនឹងវិធានការសុវត្ថិភាពថ្នាក់សហគ្រាស។" },
          { type:"ul", items:[
            "ពាក្យសម្ងាត់ត្រូវបាន hash ដោយប្រើ Firebase Authentication — យើងមិនដែលឃើញពាក្យសម្ងាត់អត្ថបទធម្មតារបស់អ្នកទេ",
            "ការបញ្ជូនទិន្នន័យទាំងអស់ប្រើការអ៊ិនគ្រីប HTTPS/TLS",
            "ការចូលប្រើទិន្នន័យរបស់អ្នកត្រូវបានកំណត់ចំពោះបុគ្គលិក PhsarGame ដែលមានការអនុញ្ញាតប៉ុណ្ណោះ",
            "យើងរក្សាទុកទិន្នន័យរបស់អ្នករហូតដល់គណនីរបស់អ្នកសកម្ម ឬតាមដែលចាំបាច់ដើម្បីអនុលោមតាមកាតព្វកិច្ចផ្លូវច្បាប់",
          ]},
          { type:"p", text:"ទោះបីជាយើងខិតខំយ៉ាងដូចម្ដេចក្ដី គ្មានប្រព័ន្ធសុវត្ថិភាពណាដែលមិនអាចទ្លុះបានទេ។ ក្នុងករណីមានការបំពានទិន្នន័យ យើងនឹងជូនដំណឹងដល់អ្នកប្រើប្រាស់ដែលរងផលប៉ះពាល់ភ្លាមៗ។" },
        ]
      },
      {
        id:"cookies", num:"០៦", title:"Cookies",
        body: [
          { type:"p", text:"យើងប្រើ cookies តិចតួច និងការរក្សាទុក browser ដើម្បីដំណើរការសេវាកម្មរបស់យើង:" },
          { type:"ul", items:[
            "Session cookies ដើម្បីរក្សាអ្នកឱ្យបានចូល",
            "ចំណូលចិត្តភាសាដែលរក្សាទុកក្នុង localStorage (pg_lang)",
            "token ការផ្ទៀងផ្ទាត់ Firebase",
          ]},
          { type:"p", text:"យើងមិនប្រើ cookies ផ្សាយពាណិជ្ជកម្ម pixels តាមដាន ឬ cookies វិភាគភាគីទីបីទេ។ អ្នកអាចលុប cookies តាម browser settings គ្រប់ពេល ប៉ុន្តែការនេះអាចធ្វើឱ្យអ្នកចេញពីគណនី។" },
        ]
      },
      {
        id:"rights", num:"០៧", title:"សិទ្ធិរបស់អ្នក",
        body: [
          { type:"p", text:"អ្នកមានសិទ្ធិខាងក្រោមទាក់ទងនឹងទិន្នន័យផ្ទាល់ខ្លួនរបស់អ្នក:" },
          { type:"ul", items:[
            "ចូលប្រើ — ស្នើសុំច្បាប់ចម្លងនៃទិន្នន័យផ្ទាល់ខ្លួនដែលយើងមានអំពីអ្នក",
            "ការកែប្រែ — ស្នើសុំការកែប្រែទិន្នន័យមិនត្រឹមត្រូវ ឬមិនពេញលេញ",
            "ការលុប — ស្នើសុំការលុបគណនី និងទិន្នន័យដែលពាក់ព័ន្ធ",
            "ការចល័ត — ស្នើសុំទិន្នន័យរបស់អ្នកក្នុងទម្រង់ចល័ត",
            "ការជំទាស់ — ជំទាស់ទៅនឹងរបៀបដែលយើងដំណើរការទិន្នន័យរបស់អ្នក",
          ]},
          { type:"p", text:"ដើម្បីអនុវត្តសិទ្ធិទាំងនេះ ទូទាក់យើងតាម Telegram @phsargame ឬទំព័រ Facebook របស់យើង។ យើងនឹងឆ្លើយតបក្នុងរយៈពេល ៣០ ថ្ងៃ។" },
        ]
      },
      {
        id:"children", num:"០៨", title:"ភាពឯកជនរបស់កុមារ",
        body: [
          { type:"p", text:"PhsarGame មិនមែនត្រូវបានដឹកនាំទៅកុមារក្រោម ១៣ ឆ្នាំទេ។ យើងមិនប្រមូលព័ត៌មានផ្ទាល់ខ្លួនពីកុមារក្រោម ១៣ ឆ្នាំដោយដឹងខ្លួនទេ។ ប្រសិនបើអ្នកជាឪពុក ឬម្ដាយ ហើយជឿថាកូនអ្នកបានផ្ដល់ព័ត៌មានផ្ទាល់ខ្លួនដល់យើង សូមទូទាក់ យើង ហើយយើងនឹងលុបវាភ្លាមៗ។" },
          { type:"p", text:"អ្នកប្រើប្រាស់ដែលមានអាយុចន្លោះ ១៣ ដល់ ១៨ ឆ្នាំត្រូវមានការអនុញ្ញាតពីឪពុកម្ដាយ ដើម្បីប្រើសេវាកម្ម និងធ្វើការទិញ។" },
        ]
      },
      {
        id:"third-party", num:"០៩", title:"សេវាកម្មភាគីទីបី",
        body: [
          { type:"p", text:"សេវាកម្មរបស់យើងរួមបញ្ចូលជាមួយវេទិកាភាគីទីបីខាងក្រោម:" },
          { type:"ul", items:[
            "Google Firebase — ការផ្ទៀងផ្ទាត់ និងការរក្សាទុកទិន្នន័យ (គោលការណ៍ភាពឯកជន Google អនុវត្ត)",
            "ធនាគារ ABA — ដំណើរការការទូទាត់ (គោលការណ៍ភាពឯកជន ABA អនុវត្ត)",
            "Telegram — ឆានែលដឹកជញ្ជូនការបញ្ជាទិញ និងជំនួយស្ម័គ្រចិត្ត",
            "YouTube — វីដេអូបង្រៀនដែលបានបង្កប់នៅទំព័ររបៀបប្រើ",
          ]},
          { type:"p", text:"យើងមិនទទួលខុសត្រូវចំពោះការអនុវត្តភាពឯកជនរបស់សេវាភាគីទីបីទាំងនេះទេ។ យើងលើកទឹកចិត្តអ្នកឱ្យពិនិត្យគោលការណ៍ភាពឯកជនរបស់ពួកគេ។" },
        ]
      },
      {
        id:"contact", num:"១០", title:"ទូទាក់យើង",
        body: [
          { type:"p", text:"ប្រសិនបើអ្នកមានសំណួរ ឬកង្វល់អំពីគោលការណ៍ភាពឯកជននេះ ឬការអនុវត្តទិន្នន័យរបស់យើង អ្នកអាចទំនាក់ទំនងយើងតាម:" },
          { type:"ul", items:[
            "Telegram: @phsargame",
            "Facebook: facebook.com/phsargame",
            "Chat ក្នុងកម្មវិធី: មាននៅលើគេហទំព័ររបស់យើង",
          ]},
          { type:"p", text:"យើងប្ដេជ្ញាដោះស្រាយកង្វល់ភាពឯកជនណាមួយដោយភ្លាម និងតម្លាភាព។ គោលការណ៍នេះត្រូវបានពិនិត្យចុងក្រោយក្នុងខែមករា ២០២៥។" },
        ]
      },
    ],
    contactTitle: "មានសំណួរអំពីភាពឯកជនរបស់អ្នក?",
    contactSub: "យើងចាត់ទុកភាពឯកជនយ៉ាងធ្ងន់ធ្ងរ។ ទូទាក់យើង ហើយយើងនឹងឆ្លើយតបក្នុង ២៤ ម៉ោង។",
    contactBtn: "ទូទាក់យើង",
  }
};

export default function Privacy() {
  const { lang } = useLang();
  const c = CONTENT[lang] || CONTENT.en;

  function renderBody(items) {
    return items.map((item, i) => {
      if (item.type === "p") return <p key={i}>{item.text}</p>;
      if (item.type === "highlight") return <div key={i} className="legal-highlight">{item.text}</div>;
      if (item.type === "ul") return (
        <ul key={i}>
          {item.items.map((li, j) => <li key={j}>{li}</li>)}
        </ul>
      );
      if (item.type === "table") return (
        <table key={i} className="data-table">
          <thead>
            <tr>
              <th>Data Type</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {item.rows.map(([col1, col2], j) => (
              <tr key={j}><td>{col1}</td><td>{col2}</td></tr>
            ))}
          </tbody>
        </table>
      );
      return null;
    });
  }

  return (
    <>
      <style>{S}</style>
      <div className="legal-pg">
        <Navbar />

        {/* Hero */}
        <div className="legal-hero">
          <div className="legal-hero-grid" />
          <div className="legal-hero-glow" />
          <div className="legal-hero-inner">
            <div className="legal-tag">🔒 {c.tag}</div>
            <h1>{c.title} <span>{c.accent}</span></h1>
            <p className="legal-hero-sub">{c.sub}</p>
            <div className="legal-hero-meta">
              <span className="legal-meta-item"><span className="legal-meta-dot" />{c.updated}</span>
              <span className="legal-meta-item"><span className="legal-meta-dot" />{c.effective}</span>
            </div>
          </div>
        </div>

        {/* Layout */}
        <div className="legal-layout">
          {/* TOC */}
          <aside className="legal-toc">
            <div className="legal-toc-title">Contents</div>
            <ul className="legal-toc-list">
              {c.toc.map(item => (
                <li key={item.id} className="legal-toc-item">
                  <a href={`#${item.id}`}>{item.label}</a>
                </li>
              ))}
            </ul>
          </aside>

          {/* Content */}
          <div className="legal-content">
            {c.sections.map(sec => (
              <div key={sec.id} id={sec.id} className="legal-section">
                <div className="legal-section-num">{sec.num}</div>
                <h2>{sec.title}</h2>
                {renderBody(sec.body)}
              </div>
            ))}

            {/* Contact card */}
            <div className="legal-contact">
              <div className="legal-contact-left">
                <h3>{c.contactTitle}</h3>
                <p>{c.contactSub}</p>
              </div>
              <a href="https://t.me/phsargame" target="_blank" rel="noreferrer" className="legal-contact-btn">
                {c.contactBtn}
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}